// PRD 10 §2.2 #19 / §3.5 — optional representative thumbnail. Click-to-upload
// (JPG/PNG only, ≤5MB — §4.2 [ADM.013]; checked here and re-checked by the upload
// service). Stores the returned URL (mock: a base64 data: URL) on the form; a
// remove button clears it.

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, X } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';

import { useUploadImage } from '../hooks/use-health-encyclopedia-mutations';
import { getEncyclopediaErrorMessage } from '../utils/error-message';
import { IMAGE_ACCEPT, isImageFileValid } from '../utils/validate-form';
import { ThumbnailWithFallback } from './EncyclopediaThumbnail';

interface EncyclopediaThumbnailUploadProps {
  value: string | null;
  disabled?: boolean;
  onChange: (url: string | null) => void;
}

export function EncyclopediaThumbnailUpload({
  value,
  disabled = false,
  onChange,
}: EncyclopediaThumbnailUploadProps) {
  const { t } = useTranslation('health-encyclopedia');
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    // PRD §4.2 / §8.1 — 썸네일도 JPG/PNG만·최대 5MB [ADM.013], the same rule the image BLOCK
    // uses. Pre-checked client-side for instant feedback; the BE re-validates authoritatively.
    if (!isImageFileValid(file)) {
      toast.error(t('errors.UPLOAD_FAILED'));
      return;
    }
    try {
      const { url } = await upload.mutateAsync(file);
      onChange(url);
    } catch (e) {
      toast.error(getEncyclopediaErrorMessage(e, t, 'errors.UPLOAD_FAILED'));
    }
  };

  return (
    <div>
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        style={{
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
          textAlign: 'center',
          background: 'var(--muted)',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {/* SHCT-716 — editing content whose CDN object was deleted used to render a
            broken-image glyph here too. The drop zone already supplies the muted
            background, so the fallback keeps its own transparent. */}
        {value ? (
          // SHCT-649 — PRD 10 v2.16 §4.5 fixes the ratio at 16:9 and the crop at 중앙 기준, with
          // no letterboxing. The preview therefore shows the CROPPED result rather than the whole
          // upload: a portrait photo loses its top and bottom in the patient app's full-bleed hero,
          // and the operator has to see that here, while they can still swap the file.
          <ThumbnailWithFallback
            url={value}
            alt={t('thumbnail.previewAlt')}
            style={{
              width: '100%',
              maxWidth: 284,
              aspectRatio: '16 / 9',
              objectFit: 'cover',
              objectPosition: 'center',
              borderRadius: 6,
              display: 'block',
              margin: '0 auto 8px',
              background: 'transparent',
            }}
          />
        ) : (
          <ImagePlus size={24} color="var(--muted-foreground)" style={{ display: 'block', margin: '0 auto 8px' }} />
        )}
        <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: 0 }}>
          {upload.isPending ? '…' : value ? t('thumbnail.replace') : t('thumbnail.uploadHint')}
        </p>
        {/* §4.5 — the recommended spec sits with the hint, not in a tooltip: it is the thing the
            operator needs BEFORE choosing a file. */}
        <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '4px 0 0' }}>
          {t('thumbnail.specHint')}
        </p>
      </div>
      {value ? (
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          style={{ marginTop: 6, color: 'var(--destructive)' }}
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          <X size={14} /> {t('thumbnail.remove')}
        </button>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
